import { useRef, useState } from 'react';
import {
	Button,
	Box,
	CircularProgress,
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';
import { useTheme } from '../contexts/ThemeContext';

const ProductPDFGenerator = ({ products }) => {
	const { theme } = useTheme();

	const [isGenerating, setIsGenerating] = useState(false);
	const renderRef = useRef(null);

	const preloadImage = (src) => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = () => {
				const placeholderImg = new Image();
				placeholderImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDMwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMjUgMTI1SDE3NVYxNzVIMTI1VjEyNVoiIGZpbGw9IiNEREREREQiLz4KPHN2Zz4K';
				placeholderImg.onload = () => resolve(placeholderImg);
			};
			img.src = src;
		});
	};

	const generatePDF = async () => {
		setIsGenerating(true);
		try {
			const productsPerPage = 8;
			const pdfDoc = await PDFDocument.create();
			const totalPages = Math.ceil(products.length / productsPerPage);

			for (let i = 0; i < products.length; i += productsPerPage) {
				const chunk = products.slice(i, i + productsPerPage);
				const pageNumber = Math.floor(i / productsPerPage) + 1;

				const pageElement = document.createElement('div');
				pageElement.style.cssText = `
					width: 180mm;
					min-height: 297mm;
					padding: 20mm;
					background: white;
					font-family: ${theme.fontFamily};
					position: absolute;
					top: -9999px;
					left: -9999px;
				`;

				pageElement.innerHTML = `
				<div style="margin-bottom: 30px; text-align: center; border-bottom: 2px solid #1976d2; padding-bottom: 15px; font-family: ${theme.fontFamily};">
					<p style="margin: 0; color: #666; font-size: 14px;">Page ${pageNumber} of ${totalPages}</p>
				</div>
				
				<!-- Table Header -->
				<div style="display: grid; grid-template-columns: 80px 1fr 100px 50px 120px; font-family: ${theme.fontFamily}; gap: 15px; padding: 12px 15px; background: #f8f9fa; border: 1px solid #dee2e6; border-bottom: 2px solid #1976d2; font-weight: 600; color: #333; margin-bottom: 0;">
					<div style="text-align: center;">Image</div>
					<div>Product Title</div>
					<div style="text-align: center;">Price</div>
					<div style="text-align: center;">Qty</div>
					<div style="text-align: center;">UPC</div>
				</div>
				
				<div id="products-table" style="border: 1px solid #dee2e6; border-top: none; margin-bottom: 40px;"></div>
				
				<div style="position: absolute; bottom: 20px; left: 20mm; right: 20mm; text-align: center; padding-top: 15px; border-top: 1px solid #e0e0e0;">
					<small style="color: #666;">Generated on ${new Date().toLocaleDateString()} | Total Products: ${products.length}</small>
				</div>
			`;

				const productsTable = pageElement.querySelector('#products-table');

				for (let j = 0; j < chunk.length; j++) {
					const product = chunk[j];
					const productRow = document.createElement('div');

					const isEven = j % 2 === 0;
					productRow.style.cssText = `
						display: grid;
						grid-template-columns: 80px 1fr 100px 50px 120px;
						gap: 15px;
						padding: 12px 15px;
						border-bottom: 1px solid #dee2e6;
						background: ${isEven ? '#ffffff' : '#f8f9fa'};
						align-items: center;
						min-height: 80px;
					`;

					let imgElement;
					try {
						imgElement = await preloadImage(product.image);
					} catch (error) {
						imgElement = new Image();
						imgElement.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjREREREREIi8+Cjwvc3ZnPgo=';
					}

					productRow.innerHTML = `
						<!-- Image Column -->
						<div style="display: flex; justify-content: center; align-items: center;">
							<img src="${imgElement.src}" 
									style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; border: 1px solid #dee2e6;" 
									crossorigin="anonymous" />
						</div>
						
						<!-- Title Column -->
						<div style="padding-right: 10px;">
							<div style="font-size: 14px; font-family: ${theme.fontFamily}; font-weight: 600; color: #333; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">
								${product.title}
							</div>
						</div>
						
						<!-- Price Column -->
						<div style="text-align: center;">
							<span style="font-size: 16px; font-weight: 700; color: #1976d2; font-family: ${theme.fontFamily};">
								$${typeof product.price === 'object' ? product.price.amount : product.price}
							</span>
						</div>
						
						<!-- Quantity Column -->
						<div style="text-align: center;">
							<span style="font-size: 14px; color: black; font-family: ${theme.fontFamily}; padding: 4px 8px; font-weight: bold;">
									${product.qty}
							</span>
						</div>

						<!-- UPC Column -->
						<div style="text-align: center;">
							${product.upc ?
							`<span style="font-size: 14px; color: black; font-family: ${theme.fontFamily}; padding: 4px 8px; font-weight: bold;">
									${product.upc}
								</span>` :
							`<span style="color: #999; font-size: 12px;">N/A</span>`
						}
						</div>
					`;

					productsTable.appendChild(productRow);
				}

				document.body.appendChild(pageElement);

				// Wait for images to load
				await new Promise(resolve => setTimeout(resolve, 800));

				const canvas = await html2canvas(pageElement, {
					width: 794, // A4 width in pixels at 96 DPI
					height: 1123, // A4 height in pixels at 96 DPI
					scale: 2,
					useCORS: true,
					allowTaint: true,
					backgroundColor: '#ffffff'
				});

				const imgData = canvas.toDataURL('image/png');
				const imageBytes = await fetch(imgData).then(res => res.arrayBuffer());
				const embeddedImage = await pdfDoc.embedPng(imageBytes);

				const page = pdfDoc.addPage([595.28, 841.89]); // A4 size in points
				const { width, height } = page.getSize();

				page.drawImage(embeddedImage, {
					x: 0,
					y: 0,
					width: width,
					height: height,
				});
				document.body.removeChild(pageElement);
			}

			const pdfBytes = await pdfDoc.save();
			const blob = new Blob([pdfBytes], { type: 'application/pdf' });
			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = `product-catalog-${new Date().toISOString().split('T')[0]}.pdf`;
			link.click();
		} catch (error) {
			console.error('Error generating PDF:', error);
			alert('Error generating PDF. Please try again.');
		} finally {
			setIsGenerating(false);
		}
	};

	return (
		<Box>
			<Button
				variant="contained"
				size="small"
				startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
				onClick={generatePDF}
				disabled={isGenerating || products.length === 0}
				sx={{
					py: 0.5,
					fontFamily: theme.fontFamily,
					backgroundColor: theme.color.primary,
					borderRadius: '20px',
					textTransform: "none",
					'&.Mui-disabled': {
						opacity: 0.5,
						color: theme.text.negative,
						backgroundColor: theme.accent.disabled,
					}
				}}
			>
				{isGenerating ? 'Generating PDF...' : 'Generate PDF'}
			</Button>
			<div
				ref={renderRef}
				style={{
					position: 'absolute',
					top: '-9999px',
					left: '-9999px',
					width: '210mm',
					background: '#fff',
				}}
			/>
		</Box>
	);
};

export default ProductPDFGenerator;